import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from 'lucide-react';
import * as api from '../api/client';
import useGlobals from '../useGlobals';
import { useAdminPageBridge } from './AdminPageContext';
import BlockRenderer from '../blocks/BlockRenderer';

function genUid() {
  return (
    'b_' +
    Math.random().toString(36).slice(2, 8) +
    Date.now().toString(36).slice(-4)
  );
}

// Normalize backend block rows so every block has a stable `uid` field.
// Backend persists `block_uid`; downstream renderer/editor code uses `uid`.
function normalizeBlocks(rawBlocks) {
  return (rawBlocks || []).map((b) => ({
    ...b,
    uid: b.uid || b.block_uid || genUid(),
    block_uid: b.block_uid || b.uid,
  }));
}

export default function AdminPage() {
  const { slug } = useParams();
  const bridge = useAdminPageBridge();
  const seedGlobals = useGlobals();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(null);
    setBlocks([]);
    api
      .getPage(slug)
      .then((resp) => {
        if (cancelled) return;
        // Response shapes accepted:
        //   { page: {...}, blocks: [...] }
        //   { page: { blocks: [...] } }
        //   { blocks: [...] }  / raw page object
        const p =
          (resp && resp.page) ||
          (resp && resp.data && resp.data.page) ||
          resp ||
          null;
        const rawBlocks =
          (resp && Array.isArray(resp.blocks) && resp.blocks) ||
          (p && Array.isArray(p.blocks) && p.blocks) ||
          [];
        setPage(p);
        setBlocks(normalizeBlocks(rawBlocks));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err && err.message ? err.message : 'Failed to load page.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const setBlocksRef = useRef(setBlocks);
  setBlocksRef.current = setBlocks;

  useEffect(() => {
    if (!page) return undefined;
    const descriptor = {
      slug,
      title: page.title,
      metaDescription: page.meta_description,
      blocks,
      setBlocks: (updater) => setBlocksRef.current(updater),
    };
    const unregister = bridge.registerPage(descriptor);
    return unregister;
  }, [bridge, slug, page, blocks]);

  const onChangeBlock = useCallback(
    (uid, newData) => {
      bridge.applyBlockChange(uid, newData);
    },
    [bridge]
  );

  const onReorderBlocks = useCallback((newOrder) => {
    setBlocks(newOrder);
  }, []);

  const onAddBlock = useCallback((blockType, atIndex) => {
    setBlocks((prev) => {
      const next = [...(prev || [])];
      const uid = genUid();
      const block = { uid, block_uid: uid, block_type: blockType, data: {}, is_visible: true };
      const idx = typeof atIndex === 'number' ? atIndex : next.length;
      next.splice(idx, 0, block);
      return next;
    });
  }, []);

  const onDeleteBlock = useCallback((uid) => {
    setBlocks((prev) => (prev || []).filter((b) => b && b.uid !== uid));
  }, []);

  const onDuplicateBlock = useCallback((uid) => {
    setBlocks((prev) => {
      const list = prev || [];
      const i = list.findIndex((b) => b && b.uid === uid);
      if (i === -1) return list;
      const newUid = genUid();
      const copy = { ...list[i], uid: newUid, block_uid: newUid };
      const next = [...list];
      next.splice(i + 1, 0, copy);
      return next;
    });
  }, []);

  const onToggleVisibility = useCallback((uid) => {
    setBlocks((prev) =>
      (prev || []).map((b) =>
        b && b.uid === uid ? { ...b, is_visible: b.is_visible === false ? true : false } : b
      )
    );
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 text-sm">
        <Loader size={16} className="animate-spin mr-2" />
        Loading {slug}…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 my-6">
        <div className="font-semibold mb-1">Could not load page</div>
        <div>{error}</div>
      </div>
    );
  }

  // Render the page exactly like the public site: globals.navbar + blocks + globals.footer.
  // BlockRenderer's `withGlobals` switch handles navbar/footer wrapping.
  return (
    <BlockRenderer
      blocks={blocks}
      globals={seedGlobals}
      withGlobals={true}
      editing={true}
      onChangeBlock={onChangeBlock}
      onReorderBlocks={onReorderBlocks}
      onAddBlock={onAddBlock}
      onDeleteBlock={onDeleteBlock}
      onDuplicateBlock={onDuplicateBlock}
      onToggleVisibility={onToggleVisibility}
    />
  );
}
